import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, EMPTY, lastValueFrom, map, take } from 'rxjs';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { VimeoVideoTag } from '@ag-common-lib/lib/models/utils/vimeo-video.model';
import { sortBy } from 'lodash';
import { SortDirectionType } from '@ag-common-lib/lib/models/utils/sorting.model';

export enum VimeoSortingKeys {
  alphabetical = 'alphabetical',
  date = 'date',
  default = 'default',
}

export interface SearchVideoInFolderPayload {
  page?: string;
  per_page?: string;
  fields?: string;
  query?: string;
  query_fields?: string;
  filter_tag?: string;
  filter_tag_all_of?: string;
  filter_tag_exclude?: string;
  include_subfolders?: boolean;
  sort?: VimeoSortingKeys;
  direction?: SortDirectionType;
}

@Injectable()
export class VimeoService {
  private accessToken = 'd6dd4115affc43f2f62be38d58150ce7';
  private baseApiUrl = 'https://api.vimeo.com';
  private meApiUrl = 'https://api.vimeo.com/me';

  constructor(private http: HttpClient) {}

  getVideos(page: number = 1, perPage: number = 10, searchQuery: string = ''): Promise<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });

    const params: any = {
      page: page.toString(),
      per_page: perPage.toString(),
    };

    if (searchQuery) {
      params.query = searchQuery;
    }

    const response = this.http.get<any>([this.meApiUrl, 'videos'].join('/'), { headers, params });

    return lastValueFrom(response.pipe(take(1)));
  }

  getVideoThumbnails(path: string): Promise<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });
    const id = path?.replace(/\D/g, '');
    const staticThumbnailsUrl = [this.baseApiUrl, 'videos', id, 'pictures'].join('/');
    const staticThumbnailsResponse = this.http.get<any>(staticThumbnailsUrl, { headers }).pipe(
      catchError(e => {
        return EMPTY;
      }),
    );

    const animatedThumbnailsUrl = [this.baseApiUrl, 'videos', id, 'animated_thumbsets'].join('/');
    const animatedThumbnailsResponse = this.http.get<any>(animatedThumbnailsUrl, { headers }).pipe(
      catchError(e => {
        return EMPTY;
      }),
    );

    return lastValueFrom(staticThumbnailsResponse.pipe(take(1)));
  }

  getVideoById(path: string): Promise<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });

    const response = this.http.get<any>(`${this.meApiUrl}${path}`, { headers }).pipe(
      catchError(e => {
        return EMPTY;
      }),
    );

    return lastValueFrom(response.pipe(take(1)));
  }

  videoDataSource = new DataSource({
    paginate: true,
    pageSize: 15,
    store: new CustomStore({
      key: 'uri',
      byKey: uri => this.getVideoById(uri),
      load: async loadOptions => {
        const page = Math.ceil((loadOptions.skip || 0) / (loadOptions.take || 10)) + 1;
        const pageSize = loadOptions.take || 20;

        return this.getVideos(page, pageSize, loadOptions?.searchValue)

          .then(response => {
            return {
              data: response.data, // Vimeo API returns the videos in `data`
              totalCount: response.total, // Total video count for pagination
            };
          })
          .catch(error => {
            throw error;
          });
      },
    }),
  });

  getVideosByFolder(projectId: string) {
    const headers = {
      Authorization: `Bearer ${this.accessToken}`,
    };

    const response = this.http.get<any>([this.meApiUrl, 'projects', projectId, 'videos'].join('/'), { headers });

    return lastValueFrom(response.pipe(take(1)));
  }

  getFolders(page: number = 1, perPage: number = 10, searchQuery: string = '') {
    const headers = {
      Authorization: `Bearer ${this.accessToken}`,
    };

    const params: any = {
      page: page.toString(),
      per_page: perPage.toString(),
      fields: 'uri,name,project_id',
    };

    if (searchQuery) {
      params.query = searchQuery;
    }

    const response = this.http.get<any>([this.meApiUrl, 'projects'].join('/'), { headers, params });

    return lastValueFrom(response.pipe(take(1)));
  }

  getFolderById(mediaFolderId: string): Promise<any> {
    const id = mediaFolderId?.split('/')?.at(-1);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });

    const response = this.http.get<any>([this.meApiUrl, 'projects', id].join('/'), { headers }).pipe(
      map(data => {
        return data;
      }),
      catchError(e => {
        return EMPTY;
      }),
    );

    return lastValueFrom(response.pipe(take(1)));
  }

  foldersDataSource = new DataSource({
    paginate: true,
    pageSize: 15,
    store: new CustomStore({
      key: 'uri',
      byKey: uri => this.getFolderById(uri),
      load: async loadOptions => {
        const page = Math.ceil((loadOptions.skip || 0) / (loadOptions.take || 10)) + 1;
        const pageSize = loadOptions.take || 20;

        return this.getFolders(page, pageSize, loadOptions?.searchValue)

          .then(response => {
            return {
              data: response.data, // Vimeo API returns the videos in `data`
              totalCount: response.total, // Total video count for pagination
            };
          })
          .catch(error => {
            throw error;
          });
      },
    }),
  });

  getVideosInFolder(folderId: string, payload: SearchVideoInFolderPayload): Promise<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });

    const params: SearchVideoInFolderPayload = Object.assign(
      {
        page: '1',
        per_page: '10',
        // query_fields: 'uri,name,description',
      },
      payload,
    );

    let queryParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== 'null' && value !== 'undefined') {
        queryParams = queryParams.append(key, value);
      }
    });

    const response = this.http.get<any>([this.meApiUrl, 'projects', folderId, 'videos'].join('/'), {
      headers,
      params: queryParams,
    });
    // const response = this.http.get<any>(`${this.baseApiUrl}${folderPath}/videos`, { headers, params: queryParams });

    return lastValueFrom(response.pipe(take(1)));
  }

  getSubFolders(
    folderId: string,
    page: number = 1,
    perPage: number = 10,
    searchQuery: string = '',
    filter: 'folder' = null,
  ) {
    const headers = {
      Authorization: `Bearer ${this.accessToken}`,
    };

    const params: any = {
      page: page.toString(),
      per_page: perPage.toString(),
    };

    if (filter) {
      params.filter = filter;
    }

    // No query params for GET "projects/{project_id}/items"
    if (searchQuery) {
      params.query = searchQuery;
    }

    const response = this.http.get<any>([this.meApiUrl, 'projects', folderId, 'items'].join('/'), { headers, params });

    return lastValueFrom(response.pipe(take(1)));
  }

  getAllSubfolders = async mediaUri => {
    let page = 1;
    const folderId: any = +mediaUri.split('/').pop();
    const data = [];
    const getItems = async () => {
      try {
        const response = await this.getSubFolders(folderId, page, 100, null, 'folder');

        if (!Array.isArray(response?.data) || !response?.data?.length) {
          return;
        }

        response?.data?.forEach(item => {
          const folder = item?.folder;
          data.push(folder);
        });

        if (!response?.paging?.next) {
          return;
        }

        page++;
        return getItems();
      } catch (error) {}
      return;
    };

    await getItems();

    return data;
  };

  getSubFolderById(path: string): Promise<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });

    const response = this.http.get<any>(`${this.baseApiUrl}${path}`, { headers }).pipe(
      map(data => {
        return data;
      }),
      catchError(e => {
        return EMPTY;
      }),
    );

    return lastValueFrom(response.pipe(take(1)));
  }

  getAllFolders(page: number = 1, perPage: number = 10, searchQuery: string = '') {
    const headers = {
      Authorization: `Bearer ${this.accessToken}`,
    };

    const params: any = {
      page: page.toString(),
      per_page: perPage.toString(),
      fields: 'uri,name,has_subfolder,created_time,metadata.connections',
    };

    if (searchQuery) {
      params.query = searchQuery;
    }

    const response = this.http.get<any>([this.meApiUrl, 'projects'].join('/'), { headers, params });

    return lastValueFrom(response.pipe(take(1)));
  }

  getVideoByIdWithParams(path: string, fields?: string): Promise<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });
    const params: any = {
      fields: 'uri, name, type, height, width, description',
    };
    if (fields) {
      params.fields = params.fields.concat(`, ${fields}`);
    }

    const response = this.http.get<any>(`${this.meApiUrl}${path}`, { headers, params }).pipe(
      catchError(e => {
        return EMPTY;
      }),
    );

    return lastValueFrom(response.pipe(take(1)));
  }

  getMediaItemByIdWithParams(path: string, fields?: string): Promise<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });
    const params: any = {
      fields: 'uri, name, type, height, width, description',
    };
    if (fields) {
      params.fields = params.fields.concat(`, ${fields}`);
    }

    const response = this.http.get<any>(`${this.meApiUrl}${path}`, { headers, params }).pipe(
      catchError(e => {
        return EMPTY;
      }),
    );

    return lastValueFrom(response.pipe(take(1)));
  }

  getMediaItems(page: number = 1, perPage: number = 10, searchQuery: string = '', fields?: string) {
    const headers = {
      Authorization: `Bearer ${this.accessToken}`,
    };

    const params: any = {
      page: page.toString(),
      per_page: perPage.toString(),
      fields:
        'type,video.id,video.name,video.uri,video.last_user_action_event_date,video.link,video.review_page.link,video.pictures,video.metadata.interactions.edit,video.manage_link,video.privacy.view,meta,folder.name,folder.uri,folder.last_user_action_event_date,folder.metadata.connections.items.uri,folder.metadata.connections.items.total',
      sort: 'last_user_action_event_date',
      precision: 3,
    };

    if (fields) {
      params.fields = params.fields.concat(`, ${fields}`);
    }

    if (searchQuery) {
      params.query = searchQuery;
    }

    const response = this.http.get<any>([this.meApiUrl, 'items'].join('/'), { headers, params });

    return lastValueFrom(response.pipe(take(1)));
  }

  getAllVideosSubFolders(
    folderId: string,
    page: number = 1,
    perPage: number = 10,
    searchQuery: string = '',
    searchTags?: string,
  ) {
    const headers = {
      Authorization: `Bearer ${this.accessToken}`,
    };

    const params: any = {
      page: page.toString(),
      per_page: perPage.toString(),
      include_subfolders: true,
    };

    // No query params for GET "projects/{project_id}/items"
    if (searchQuery && !searchTags) {
      params.query = searchQuery;
    }

    // get all videos in folder and filer by tag
    if (searchTags) {
      params.filter_tag = searchTags;
    }

    const response = this.http.get<any>([this.meApiUrl, 'projects', folderId, 'videos'].join('/'), { headers, params });

    return lastValueFrom(response.pipe(take(1)));
  }

  /*** VIDEO Tags ***/
  private getTagsFromPage(page: number, perPage: number = 30) {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });

    const params: any = {
      page: page.toString(),
      per_page: perPage.toString(),
      fields: 'tags',
    };

    return lastValueFrom(this.http.get<any>(`${this.meApiUrl}/videos`, { headers, params }));
  }

  async getAllTags(perPage: number = 30): Promise<VimeoVideoTag[]> {
    let allTags: VimeoVideoTag[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await this.getTagsFromPage(page, perPage);

      response.data.forEach(video => {
        if (video.tags && video.tags.length) {
          video.tags.forEach(tag => {
            if (tag.tag && !allTags.some(item => item.tag === tag.tag)) {
              allTags.push(tag);
            }
          });
        }
      });

      totalPages = response.paging.last ? this.extractPageNumber(response.paging.last) : page;

      page++;
    } while (page <= totalPages);

    return sortBy(allTags, item => item.tag.toLowerCase());
  }

  private extractPageNumber(url: string): number {
    const match = url.match(/page=(\d+)/);
    return match ? parseInt(match[1], 10) : 1;
  }

  /*
   * Important points for tags:
   * To work with the Vimeo API, you need a personal access token with the necessary rights (public, private, create, edit, upload).
   * Tags cannot be added in the same request where other video properties are updated (for example, name or description) - tags require a separate PUT request.
   */
  addTagsToVideo(videoId: string, tags: string[]) {
    const url = `https://api.vimeo.com/videos/${videoId}/tags`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
      'Content-Type': 'application/json',
      Accept: 'application/vnd.vimeo.*+json;version=3.4',
    });

    /* Body: {
      "tags": [
        {"tag": "tag1"},
        {"tag": "tag2"},
        {"tag": "tag3"}
      ]
    }*/
    const body = {
      tags: tags.map(tag => ({ tag })),
    };

    return this.http.put(url, body, { headers });
  }
}
